import React, { useEffect, useState, useCallback } from "react";
import { Check, X, RefreshCw, ExternalLink, Copy, Search } from "lucide-react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const statusStyle = {
  Pending: "text-amber-400 bg-amber-400/10",
  Confirming: "text-amber-400 bg-amber-400/10",
  Completed: "text-emerald-400 bg-emerald-400/10",
  Rejected: "text-rose-400 bg-rose-400/10",
};

const explorerLink = (method, txid) => {
  if (!txid) return null;
  if (method === "btc") return `https://mempool.space/tx/${txid}`;
  // usdt-trc20 + trx -> tronscan
  if (method === "usdt" || method === "trx") return `https://tronscan.org/#/transaction/${txid}`;
  return null;
};

const CopySmall = ({ value }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-white/30">—</span>;
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
        } catch { /* noop */ }
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="inline-flex items-center gap-1 text-white/55 hover:text-white text-[11px] font-mono max-w-[180px] truncate"
      title={value}
    >
      <span className="truncate">{value}</span>
      {copied ? <Check size={11} className="text-emerald-400 shrink-0" /> : <Copy size={11} className="shrink-0" />}
    </button>
  );
};

const AdminDeposits = () => {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("Pending");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [rowBusy, setRowBusy] = useState({});
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "All") params.status = statusFilter;
      const { data } = await api.get("/admin/deposits", { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (id) => {
    if (!window.confirm("Approve this deposit and credit the user's balance?")) return;
    setRowBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.post(`/admin/deposits/${id}/approve`, { note: null });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to approve.");
    } finally {
      setRowBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const submitReject = async () => {
    if (!rejectFor) return;
    if (rejectReason.trim().length < 2) {
      alert("Reason is required.");
      return;
    }
    setRowBusy((b) => ({ ...b, [rejectFor]: true }));
    try {
      await api.post(`/admin/deposits/${rejectFor}/reject`, { reason: rejectReason.trim() });
      setRejectFor(null);
      setRejectReason("");
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to reject.");
    } finally {
      setRowBusy((b) => ({ ...b, [rejectFor]: false }));
    }
  };

  const filtered = items.filter((i) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (i.user_email || "").toLowerCase().includes(s) ||
      (i.user_name || "").toLowerCase().includes(s) ||
      (i.id || "").toLowerCase().includes(s) ||
      (i.txid || "").toLowerCase().includes(s)
    );
  });

  return (
    <AdminLayout title="Deposits" subtitle="Approve customer funding">
      <div className="bb-card rounded-2xl overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-white/5">
          <div className="flex items-center gap-1 bg-white/[0.04] rounded-lg p-1 border border-white/8 overflow-x-auto">
            {["Pending", "Confirming", "Completed", "Rejected", "All"].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 text-[12px] rounded-md whitespace-nowrap transition-colors ${
                  statusFilter === s ? "bg-white text-[#07080d] font-medium" : "text-white/55 hover:text-white"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 h-9 bg-white/[0.04] border border-white/8 rounded-lg">
              <Search size={14} className="text-white/40" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search email, name, TX ID"
                className="bg-transparent outline-none text-[12px] text-white placeholder:text-white/35 w-56"
              />
            </div>
            <button onClick={load} className="w-9 h-9 rounded-lg border border-white/8 text-white/70 hover:text-white flex items-center justify-center">
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-[13px] min-w-[1100px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-white/40 font-semibold bg-white/[0.02]">
                <th className="text-left py-3 px-5">User</th>
                <th className="text-left py-3">Method</th>
                <th className="text-right py-3">Amount</th>
                <th className="text-left py-3">TX ID / Bank ref</th>
                <th className="text-left py-3">Submitted</th>
                <th className="text-left py-3">Status</th>
                <th className="text-right py-3 pr-5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => {
                const link = explorerLink(d.method, d.txid);
                return (
                  <tr key={d.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="py-4 px-5">
                      <div className="text-white font-medium text-[13px]">{d.user_name}</div>
                      <div className="text-white/45 text-[11px]">{d.user_email}</div>
                      <div className="text-white/30 text-[10px] font-mono">{d.id}</div>
                    </td>
                    <td>
                      <div className="text-white text-[12px]">{d.method_label || d.method}</div>
                      <div className="text-white/40 text-[10px]">{d.network || "—"}</div>
                    </td>
                    <td className="text-right text-white font-semibold tabular-nums">
                      ${fmt(d.amount)}
                    </td>
                    <td>
                      {d.txid ? (
                        <div className="flex items-center gap-2">
                          <CopySmall value={d.txid} />
                          {link && (
                            <a href={link} target="_blank" rel="noopener noreferrer" className="text-[#ffae6a] hover:text-white" title="View on explorer">
                              <ExternalLink size={12} />
                            </a>
                          )}
                        </div>
                      ) : d.bank_ref ? (
                        <span className="text-white/55 text-[12px]">{d.bank_ref}</span>
                      ) : (
                        <span className="text-white/30">—</span>
                      )}
                      {d.address && (
                        <div className="text-[10px] text-white/35 font-mono truncate max-w-[180px]" title={d.address}>
                          to: {d.address.slice(0, 8)}…{d.address.slice(-4)}
                        </div>
                      )}
                    </td>
                    <td className="text-white/65 text-[11px]">
                      {new Date(d.created_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                    </td>
                    <td>
                      <span className={`inline-block text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${statusStyle[d.status] || "text-white/55 bg-white/[0.05]"}`}>
                        {d.status}
                      </span>
                      {d.reject_reason && (
                        <div className="text-[10px] text-rose-300/70 mt-1 max-w-[160px] truncate" title={d.reject_reason}>
                          {d.reject_reason}
                        </div>
                      )}
                    </td>
                    <td className="text-right pr-5">
                      {(d.status === "Pending" || d.status === "Confirming") && (
                        <div className="inline-flex gap-1.5">
                          <button
                            onClick={() => approve(d.id)}
                            disabled={rowBusy[d.id]}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/25 text-[11px] font-medium disabled:opacity-50"
                          >
                            <Check size={12} /> Approve
                          </button>
                          <button
                            onClick={() => { setRejectFor(d.id); setRejectReason(""); }}
                            disabled={rowBusy[d.id]}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-white/10 text-white/70 hover:bg-rose-500/10 hover:border-rose-400 hover:text-rose-400 text-[11px] font-medium"
                          >
                            <X size={12} /> Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {!loading && filtered.length === 0 && (
          <div className="py-16 text-center text-[13px] text-white/40">No deposits to show.</div>
        )}
      </div>

      {/* Reject reason modal */}
      {rejectFor && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setRejectFor(null)}></div>
          <div className="relative bb-card rounded-3xl p-6 w-full max-w-md">
            <div className="font-display font-bold text-[18px] mb-1">Reject deposit</div>
            <p className="text-[12px] text-white/55 mb-4">Give a reason — the customer will see this in their transaction history.</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. TX ID could not be verified on chain."
              rows={3}
              className="w-full px-3 py-2.5 bg-white/[0.02] border border-white/10 rounded-xl text-[13px] text-white outline-none focus:border-[#ff7a1a] placeholder:text-white/30"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setRejectFor(null)}
                className="px-4 py-2 rounded-lg border border-white/10 text-white/70 text-[13px]"
              >
                Cancel
              </button>
              <button
                onClick={submitReject}
                className="px-4 py-2 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-[13px] font-medium"
              >
                Confirm reject
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDeposits;
